import * as React from 'react';
import { I18nextProvider } from 'react-i18next';
import { Provider } from 'react-redux';
import { createBrowserHistory } from 'history';
import { Store } from 'redux';

import i18next from '../../config/i18next';
import AppNegotiator from '../AppNegotiator';
import configureStore from '../../store';
import { __DEBUG__, WIDGET_OPTIONS } from '../../config/constants';
import Router from '../Router';
import AppSelector from '../AppSelector';

/**
 * We explicitly only import the design system if is is manually configured to do so.
 * This way we exclude the design system by default, saving on bundle size when the
 * widgets are being used in a context where the design system is already included.
 */
if (process.env.NODE_ENV === 'development' || WIDGET_OPTIONS?.includeDesignSystem) {
  import(/* webpackChunkName: "design-system" */ '../../styles/design-system.scss');
}

const history = createBrowserHistory({
  basename: process.env.PUBLIC_URL,
});

// The `configureStore` method is written in plain JS and
// therefor it's return value has an any type. To prevent
// typescript issues we typecast it here to be `Store`.
const store = configureStore(history) as Store;

export type AppProps = {
  id?: string | null,
};

const Container: React.FC = (props) => {
  const { children } = props;
  if (__DEBUG__) {
    return (
      <div className="container">
        {children}
      </div>
    );
  }

  return <div>{children}</div>;
};

const App: React.FC<AppProps> = (props) => {
  const { id } = props;

  return (
    <Provider store={store}>
      <I18nextProvider i18n={i18next}>
        <Router>
          <Container>
            {id ? (
              <AppNegotiator id={id} />
            ) : (
              <AppSelector />
            )}
          </Container>
        </Router>
      </I18nextProvider>
    </Provider>
  );
};

export default App;
